import { useAppDispatch } from "../../../app/hooks";
import { useSelector } from "react-redux";
import { selectUser } from "../../../redux/user/userSlice";
import { Box, Stack, Typography, Paper, Chip, Avatar, Tooltip } from "@mui/material";
import { useEffect, useState } from "react";
import { isTokenReady } from "../../../features/user/UserAvatar";
import { SchoolInfo } from "../../../redux/school/schoolSlice";
import { ClassroomInfo } from "../../../redux/classroom/classroomSlice";
import { retrieveRoutine, routineSlice, selectRoutine } from "../../../redux/routine/routineSlice";
import { retrieveRoutineItemList, RetrieveRoutineItemListRequest, routineItemListSlice, selectRoutineItemList } from "../../../redux/routine/routineItemListSlice";
import { retrieveRoutineItemOptionList, RetrieveRoutineItemOptionListRequest, routineItemOptionListSlice, selectRoutineItemOptionList } from "../../../redux/routine/routineItemOptionListSlice";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { RoutineItemInfo } from "../../../redux/routine/routineItemSlice";
import { assertThumbnailUrl, LoadState } from "../../../redux/common";
import PrizaGridBar from "../../../features/base/grid/PrizaGridBar";
import { getItemOptionColor } from "../../routine/main/RoutineItemList";
import { selectClassroomStudents } from "../../../redux/classroom/classroomStudentsSlice";
import { retrieveRoutineRecordList, RetrieveRoutineRecordListRequest, routineRecordListSlice, selectRoutineRecordList } from "../../../redux/routine/routineRecordListSlice";
import GradingIcon from "@mui/icons-material/Grading";
import { useNavigate } from "react-router";
import { routeRoutineFeed } from "../../../routes/routine";
import { hasPermissionToEditRoutine } from "../feed/RoutineFeed";
import BadgedAvatar from "../../../features/base/BadgedAvatar";

interface ContentListProps {
    school: SchoolInfo;
    classroom: ClassroomInfo;
}

const ClassroomRoutine = ({ /* school, */ classroom }: ContentListProps) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const user = useSelector(selectUser);
    const routine = useSelector(selectRoutine);
    const routineItems = useSelector(selectRoutineItemList);
    const routineItemOptions = useSelector(selectRoutineItemOptionList);
    const routineRecords = useSelector(selectRoutineRecordList);
    const [selectedDate, setSelectedDate] = useState(dayjs());
    const classroomStudents = useSelector(selectClassroomStudents);

    const canEdit = hasPermissionToEditRoutine(selectedDate, user?.priza?.info, classroom.id, classroom.schoolId);

    useEffect(() => {
        dispatch(routineSlice.actions.check(classroom.routineId));
        if (classroom.routineId && routine.state.state === LoadState.NotStarted && isTokenReady(user)) {
            dispatch(retrieveRoutine({ routineId: classroom.routineId }) );
        }
    }, [dispatch, classroom, routine, user]);

    useEffect(() => {
        if (!classroom.routineId) return;
        const request: RetrieveRoutineItemListRequest = { routineId: classroom.routineId };
        dispatch(routineItemListSlice.actions.check(request));
        if (classroom.routineId && routineItems.state.state === LoadState.NotStarted && isTokenReady(user)) {
            dispatch(retrieveRoutineItemList(request) );
        }
    }, [dispatch, user, classroom, routineItems]);

    useEffect(() => {
        if (!classroom.routineId) return;
        const request: RetrieveRoutineItemOptionListRequest = { routineId: classroom.routineId };
        dispatch(routineItemOptionListSlice.actions.check(request));
        if (classroom.routineId && routineItemOptions.state.state === LoadState.NotStarted && isTokenReady(user)) {
            dispatch(retrieveRoutineItemOptionList(request) );
        }
    }, [dispatch, user, classroom, routineItemOptions]);

    useEffect(() => {
        if (!classroom.routineId) return;
        const request: RetrieveRoutineRecordListRequest = {
            routineId: classroom.routineId,
            eventDate: selectedDate.format("YYYY-MM-DD"),
            classroomId: classroom.id
        };
        dispatch(routineRecordListSlice.actions.check(request));
        if (classroom.routineId && routineRecords.state.state === LoadState.NotStarted && isTokenReady(user)) {
            dispatch(retrieveRoutineRecordList(request) );
        }
    }, [dispatch, user, classroom, routineRecords, selectedDate]);
    
    const toolbar = {
        label: <>
        <Stack direction="row" alignItems="center" spacing={1}>
            <DatePicker
                value={selectedDate}
                onChange={(newValue) => {
                    if (newValue) setSelectedDate(newValue);
                    dispatch(routineRecordListSlice.actions.clean());
                }}
                format="DD/MM/YYYY"
                slotProps={{ textField: { size: "small", sx: { width: "150px", backgroundColor: "white" } } }}
            />
            <Typography variant="h6">
                {" Rotina " + (routine.selected?.name || "")}
            </Typography>
        </Stack>
        </>,
        buttons: [
            ...(canEdit ? [{
                label: <Tooltip title="Preencher">
                    <GradingIcon />
                </Tooltip>,
                click: () => {
                    navigate( routeRoutineFeed(classroom.schoolId, classroom.id, classroom.routineId || "", selectedDate.format("YYYY-MM-DD")) );
                }
            }] : [])
        ]
    };
    
    const retry = () => {
        dispatch(routineSlice.actions.clean());
        dispatch(routineItemListSlice.actions.clean());
        dispatch(routineItemOptionListSlice.actions.clean());
        dispatch(routineRecordListSlice.actions.clean());
    };

    const buildOptions = (item: RoutineItemInfo) => {
        const options = routineItemOptions.list?.filter(o => o.routineItemId === item.id) || [];
        console.log("Options for item", item.id, options);
        if(options.length === 0) return <Typography>Sem itens</Typography>;

        const informedCount = routineRecords.list?.filter(r =>
            r.routineItemOptionId
            && `${r.routineItemId}` === `${item.id}`
            && classroomStudents.list?.some(s => `${s.id}` === `${r.studentId}`)
        ).length || 0;
        const notInformedCount = ( classroomStudents.list?.length || 0 ) - informedCount;

        return <Stack direction="column" spacing={1} sx={{ m: 0, width: "100%" }}>
            <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "flex-start" }}>
                {options && 
                    options
                        .sort((a, b) => (a.order || 99) - (b.order || 99))
                        .map(option => (
                    <Box key={option.id} sx={{ mx: "2px", my: 1, width: "175px" }}>
                        <Chip
                            sx={{ 
                                width: "100%",
                                color: (theme) => getItemOptionColor(theme, option.color).contrastText,
                                border: (theme) => `1px solid ${getItemOptionColor(theme, option.color).contrastText}`,
                                backgroundColor: (theme) => getItemOptionColor(theme, option.color).main,
                            }}
                            avatar={<Avatar alt={option.name} src={assertThumbnailUrl(option.url)} />}
                            label={
                                <Box sx={{ width: "143px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        <Box sx={{
                                            backgroundColor: "primary.main",
                                            color: "primary.contrastText",
                                            // borderRadius: "50%",
                                            // px: "2px",
                                            py: "2px",
                                            width: "25px",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center"
                                        }}>
                                            {routineRecords.list?.filter(r => r.routineItemOptionId === option.id).length || 0}
                                        </Box>
                                        <Box>{option.name}</Box>
                                    </Stack>
                                </Box>
                            }
                            variant="outlined"
                            // color={getItemOptionColor(option.color)}
                        />
                    </Box>
                ))}
                <Box key={"not-informed"} sx={{ mx: "2px", my: 1, width: "175px" }}>
                    <Chip
                        sx={{ width: "100%" }}
                        color="primary"
                        avatar={<Avatar alt="Não informado" src="/static/images/not-informed.jpg" />}
                        label={
                            <Box sx={{ width: "143px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <Box sx={{
                                        color: notInformedCount > 0 ? "error.contrastText" : "success.contrastText",
                                        border: notInformedCount > 0 ? "error.contrastText" : "success.contrastText",
                                        backgroundColor: notInformedCount > 0 ? "error.main" : "success.main",
                                        // borderRadius: "50%",
                                        // px: "2px",
                                        py: "2px",
                                        width: "25px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center"
                                    }}>
                                        {notInformedCount}
                                    </Box>
                                    <Box>Não informado</Box>
                                </Stack>
                            </Box>}
                        variant="outlined"
                    />
                </Box>
            </Box>
        </Stack>;
    };


    const buildRoutine = () => {
        if(routineItems.state.state === LoadState.Loading) 
            return <Typography>Carregando...</Typography>;

        if(routineItems.state.state === LoadState.Failed) 
            return <Typography>Erro ao carregar rotina</Typography>;

        if(routineItems.list === undefined || routineItems.list.length === 0)
            return <Typography>Sem itens na rotina.</Typography>;

        const items = [...routineItems.list];
        items.sort((a, b) => (a.order || 999) - (b.order || 999));

        return items
        .map(item => {
            return <Paper key={item.id} sx={{ width: "100%", mb: 1 }} elevation={3}>
                <Box key={item.id} sx={{ width: "100%", p: 2 }}>
                    <Stack direction="column" alignItems="left" spacing={1} sx={{ width: "100%" }}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <BadgedAvatar
                                avatar={assertThumbnailUrl(item.url)}
                                title={item.name}
                                size="24px"
                                fontSize="small"
                            />
                            <Typography variant="h6" sx={{ }}>{item.name}</Typography>
                        </Stack>
                        {buildOptions(item)}
                    </Stack>
                </Box>
            </Paper>;
        });
    };

    let content: React.ReactNode = "";
    if(!classroom.routineId) {
        content = <Typography variant="body1" sx={{ m: 2 }}>Sem rotina associada a esta sala de aula.</Typography>;
    } else if(
        routine.state.state === LoadState.Loaded
        && routineItems.state.state === LoadState.Loaded
    ) {
        content = (
            <Box sx={{ m: 2, width: "100%" }}>
                {buildRoutine()}
            </Box>
        );
    } else {
        content = <Typography variant="body1" sx={{ m: 2 }}>Carregando...</Typography>;
    }

    return <Paper sx={{ width: "100%", minWidth: "300px" }} elevation={3}>
        <PrizaGridBar toolbar={toolbar} retry={retry} />
        <Box display={"flex"} width="100%">
            {content}
        </Box>
    </Paper>;

};

export default ClassroomRoutine;